import type {
  HttpRequest,
  HttpResponse,
  MatchReplaceRule,
} from './proxy-types';
import { Logger } from './logger';
import { parseBinaryBody } from '../electron-shared/utils/binary-body';
import { resolveEffectiveHttpVersion } from '@terra/shared-utils';

export class MatchReplaceProcessor {
  private logger = new Logger('MatchReplaceProcessor');

  private regexCache: Map<string, RegExp> = new Map();
  private readonly maxCacheSize = 500;
  private lastMatchedRuleIds: string[] = [];

  getLastMatchedRuleIds(): string[] {
    return [...this.lastMatchedRuleIds];
  }

  private resetMatchedRuleIds(): void {
    this.lastMatchedRuleIds = [];
  }

  private recordRuleMatchIfChanged(
    rule: MatchReplaceRule,
    before: HttpRequest | HttpResponse,
    after: HttpRequest | HttpResponse,
  ): void {
    if (this.didMessageChange(before, after)) {
      this.lastMatchedRuleIds.push(rule.id);
    }
  }

  private didMessageChange(
    before: HttpRequest | HttpResponse,
    after: HttpRequest | HttpResponse,
  ): boolean {
    if (before === after) {
      return false;
    }

    const isResponse =
      (before as HttpResponse).statusCode !== undefined &&
      (after as HttpResponse).statusCode !== undefined;

    if (isResponse) {
      const beforeResp = before as HttpResponse;
      const afterResp = after as HttpResponse;

      return (
        beforeResp.statusCode !== afterResp.statusCode ||
        beforeResp.statusMessage !== afterResp.statusMessage ||
        beforeResp.body !== afterResp.body ||
        beforeResp.headers !== afterResp.headers
      );
    }

    const beforeRequest = before as HttpRequest;
    const afterRequest = after as HttpRequest;

    return (
      beforeRequest.method !== afterRequest.method ||
      beforeRequest.url !== afterRequest.url ||
      beforeRequest.body !== afterRequest.body ||
      beforeRequest.headers !== afterRequest.headers
    );
  }

  private getOrCompileRegex(pattern: string, flags: string): RegExp {
    const cacheKey = `${pattern}:${flags}`;
    let regex = this.regexCache.get(cacheKey);

    if (!regex) {
      regex = new RegExp(pattern, flags);

      if (this.regexCache.size >= this.maxCacheSize) {
        const firstKey = this.regexCache.keys().next().value;

        if (firstKey) {
          this.regexCache.delete(firstKey);
        }
      }

      this.regexCache.set(cacheKey, regex);
    }

    return regex;
  }

  applyToRequest(request: HttpRequest, rules: MatchReplaceRule[]): HttpRequest {
    this.resetMatchedRuleIds();

    if (!rules?.length) {
      return request;
    }

    let modifiedRequest = { ...request };

    for (const rule of rules) {
      if (
        !rule.enabled ||
        (rule.matchLocation !== 'request' && rule.matchLocation !== 'both')
      ) {
        continue;
      }

      if (!this.matchesRuleConditions(rule, modifiedRequest)) {
        continue;
      }

      try {
        const before = modifiedRequest;

        modifiedRequest = this.processRule(
          rule,
          modifiedRequest,
          'request',
        ) as HttpRequest;
        this.recordRuleMatchIfChanged(rule, before, modifiedRequest);
      } catch (error) {
        this.logger.error(
          `Error applying match/replace rule "${rule.name}"`,
          {},
          error as Error,
        );
      }
    }

    return modifiedRequest;
  }

  applyToResponse(
    response: HttpResponse,
    request: HttpRequest,
    rules: MatchReplaceRule[],
  ): HttpResponse {
    this.resetMatchedRuleIds();

    if (!rules?.length) {
      return response;
    }

    let modifiedResponse = { ...response };

    for (const rule of rules) {
      if (
        !rule.enabled ||
        (rule.matchLocation !== 'response' && rule.matchLocation !== 'both')
      ) {
        continue;
      }

      if (!this.matchesRuleConditions(rule, request, modifiedResponse)) {
        continue;
      }

      try {
        const before = modifiedResponse;

        modifiedResponse = this.processRule(
          rule,
          modifiedResponse,
          'response',
          request,
        ) as HttpResponse;
        this.recordRuleMatchIfChanged(rule, before, modifiedResponse);
      } catch (error) {
        this.logger.error(
          `Error applying match/replace rule "${rule.name}"`,
          {},
          error as Error,
        );
      }
    }

    return modifiedResponse;
  }

  private matchesRuleConditions(
    rule: MatchReplaceRule,
    request: HttpRequest,
    response?: HttpResponse,
  ): boolean {
    const { conditions } = rule;

    if (!conditions) {
      return true;
    }

    if (conditions.urlPattern) {
      const urlMatches = this.testPattern(
        request.url,
        conditions.urlPattern,
        false,
        true,
      );

      if (!urlMatches) {
        return false;
      }
    }

    if (conditions.methods && conditions.methods.length > 0) {
      if (!conditions.methods.includes(request.method)) {
        return false;
      }
    }

    if (
      conditions.statusCodes &&
      conditions.statusCodes.length > 0 &&
      response
    ) {
      if (!conditions.statusCodes.includes(response.statusCode)) {
        return false;
      }
    }

    if (conditions.contentTypes && conditions.contentTypes.length > 0) {
      const contentTypeValue =
        request.headers['content-type'] ||
        (response ? response.headers['content-type'] : '');
      const contentType = Array.isArray(contentTypeValue)
        ? contentTypeValue[0] || ''
        : contentTypeValue || '';
      const matchesContentType = conditions.contentTypes.some((ct: string) =>
        contentType.toLowerCase().includes(ct.toLowerCase()),
      );

      if (!matchesContentType) {
        return false;
      }
    }

    return true;
  }

  private processRule(
    rule: MatchReplaceRule,
    data: HttpRequest | HttpResponse,
    type: 'request' | 'response',
    _originalRequest?: HttpRequest,
  ): HttpRequest | HttpResponse {
    const {
      matchType,
      matchPattern,
      isRegex,
      caseSensitive,
      replaceWith,
      replaceType,
    } = rule;

    let targetValue = '';

    switch (matchType) {
      case 'url': {
        if (type === 'request') {
          targetValue = (data as HttpRequest).url;
        }

        break;
      }

      case 'method': {
        if (type === 'request') {
          targetValue = (data as HttpRequest).method;
        }

        break;
      }

      case 'header': {
        return this.processHeaderRule(rule, data);
      }

      case 'addHeader': {
        const newHeaderName = matchPattern;
        const newHeaderValue = replaceWith;

        return {
          ...data,
          headers: {
            ...data.headers,
            [newHeaderName]: newHeaderValue,
          },
          rawHeaders: this.addRawHeader(
            data.rawHeaders,
            newHeaderName,
            newHeaderValue,
          ),
        };
      }

      case 'removeHeader': {
        const headers = { ...data.headers };
        let removed = false;

        for (const hdrName of Object.keys(headers)) {
          if (this.testPattern(hdrName, matchPattern, isRegex, caseSensitive)) {
            delete headers[hdrName];
            removed = true;
            this.logger.debug(
              `Match/Replace: "${rule.name}" removed header "${hdrName}"`,
            );
          }
        }

        if (!removed) {
          return data;
        }

        return {
          ...data,
          headers,
          rawHeaders: this.removeRawHeader(
            data.rawHeaders,
            matchPattern,
            isRegex,
            caseSensitive,
          ),
        };
      }

      case 'paramName': {
        if (type === 'request') {
          return this.processParamNameRule(rule, data as HttpRequest);
        }

        return data;
      }

      case 'paramValue': {
        if (type === 'request') {
          return this.processParamValueRule(rule, data as HttpRequest);
        }

        return data;
      }

      case 'firstLine': {
        return this.processFirstLineRule(rule, data, type);
      }

      case 'body': {
        targetValue = data.body || '';
        if (parseBinaryBody(targetValue).bodyEncoding === 'base64') {
          return data;
        }

        break;
      }

      case 'statusCode': {
        if (type === 'response') {
          targetValue = (data as HttpResponse).statusCode.toString();
        }

        break;
      }

      default:
        return data;
    }

    if (!this.testPattern(targetValue, matchPattern, isRegex, caseSensitive)) {
      return data;
    }

    const newValue = this.performReplacement(
      targetValue,
      matchPattern,
      replaceWith,
      replaceType,
      isRegex,
      caseSensitive,
    );

    if (newValue !== targetValue) {
      const modifiedData = { ...data };

      switch (matchType) {
        case 'url':
          (modifiedData as HttpRequest).url = newValue;
          break;
        case 'method':
          (modifiedData as HttpRequest).method = newValue;
          break;
        case 'body':
          modifiedData.body = newValue;
          break;
        case 'statusCode':
          (modifiedData as HttpResponse).statusCode =
            parseInt(newValue) || (modifiedData as HttpResponse).statusCode;
          break;
      }

      this.logger.debug(
        `Match/Replace: "${rule.name}" applied to ${matchType}`,
        { from: targetValue, to: newValue },
      );

      return modifiedData;
    }

    return data;
  }

  private processHeaderRule(
    rule: MatchReplaceRule,
    data: HttpRequest | HttpResponse,
  ): HttpRequest | HttpResponse {
    const {
      matchPattern,
      isRegex,
      caseSensitive,
      replaceWith,
      replaceType,
      headerName: targetHeaderName,
    } = rule;

    const headers = data.headers;

    for (const [hdrName, headerValue] of Object.entries(headers)) {
      if (targetHeaderName) {
        const headerMatches = caseSensitive
          ? hdrName === targetHeaderName
          : hdrName.toLowerCase() === targetHeaderName.toLowerCase();

        if (!headerMatches) {
          continue;
        }
      }

      const values = Array.isArray(headerValue) ? headerValue : [headerValue];

      for (const value of values) {
        if (this.testPattern(value, matchPattern, isRegex, caseSensitive)) {
          const newValue = this.performReplacement(
            value,
            matchPattern,
            replaceWith,
            replaceType,
            isRegex,
            caseSensitive,
          );

          if (newValue !== value) {
            const updatedValue = Array.isArray(headerValue)
              ? headerValue.map((v) => (v === value ? newValue : v))
              : newValue;

            this.logger.debug(
              `Match/Replace: "${rule.name}" applied to header "${hdrName}"`,
              { from: value, to: newValue },
            );

            const updatedRawHeaders = this.patchRawHeaderValue(
              data.rawHeaders,
              hdrName,
              value,
              newValue,
            );

            return {
              ...data,
              headers: {
                ...data.headers,
                [hdrName]: updatedValue,
              },
              ...(updatedRawHeaders ? { rawHeaders: updatedRawHeaders } : {}),
            };
          }
        }
      }
    }

    return data;
  }

  private processParamNameRule(
    rule: MatchReplaceRule,
    request: HttpRequest,
  ): HttpRequest {
    const { matchPattern, isRegex, caseSensitive, replaceWith, replaceType } =
      rule;

    try {
      const url = new URL(request.url);
      const params = new URLSearchParams(url.search);
      const newParams = new URLSearchParams();
      let modified = false;

      for (const [paramName, paramValue] of params.entries()) {
        if (this.testPattern(paramName, matchPattern, isRegex, caseSensitive)) {
          const newParamName = this.performReplacement(
            paramName,
            matchPattern,
            replaceWith,
            replaceType,
            isRegex,
            caseSensitive,
          );

          newParams.append(newParamName, paramValue);
          if (newParamName !== paramName) {
            modified = true;
            this.logger.debug(
              `Match/Replace: "${rule.name}" renamed param "${paramName}" to "${newParamName}"`,
            );
          }
        } else {
          newParams.append(paramName, paramValue);
        }
      }

      if (modified) {
        url.search = newParams.toString();

        return { ...request, url: url.toString() };
      }
    } catch (error) {
      this.logger.error(
        'Failed to parse URL for param replacement',
        {},
        error as Error,
      );
    }

    return request;
  }

  private processParamValueRule(
    rule: MatchReplaceRule,
    request: HttpRequest,
  ): HttpRequest {
    const {
      matchPattern,
      isRegex,
      caseSensitive,
      replaceWith,
      replaceType,
      headerName: targetParamName,
    } = rule;

    try {
      const url = new URL(request.url);
      const params = new URLSearchParams(url.search);
      const newParams = new URLSearchParams();
      let modified = false;

      for (const [paramName, paramValue] of params.entries()) {
        if (targetParamName && paramName !== targetParamName) {
          newParams.append(paramName, paramValue);
          continue;
        }

        if (
          this.testPattern(paramValue, matchPattern, isRegex, caseSensitive)
        ) {
          const newParamValue = this.performReplacement(
            paramValue,
            matchPattern,
            replaceWith,
            replaceType,
            isRegex,
            caseSensitive,
          );

          newParams.append(paramName, newParamValue);
          if (newParamValue !== paramValue) {
            modified = true;
            this.logger.debug(
              `Match/Replace: "${rule.name}" changed param "${paramName}" value`,
              { from: paramValue, to: newParamValue },
            );
          }
        } else {
          newParams.append(paramName, paramValue);
        }
      }

      if (modified) {
        url.search = newParams.toString();

        return { ...request, url: url.toString() };
      }
    } catch (error) {
      this.logger.error(
        'Failed to parse URL for param replacement',
        {},
        error as Error,
      );
    }

    return request;
  }

  private processFirstLineRule(
    rule: MatchReplaceRule,
    data: HttpRequest | HttpResponse,
    type: 'request' | 'response',
  ): HttpRequest | HttpResponse {
    const { matchPattern, isRegex, caseSensitive, replaceWith, replaceType } =
      rule;

    if (type === 'request') {
      const request = data as HttpRequest;
      const firstLine = `${request.method} ${request.url} ${resolveEffectiveHttpVersion(
        {
          httpVersion: request.httpVersion,
          headers: request.rawHeaders ?? request.headers,
        },
      )}`;

      if (this.testPattern(firstLine, matchPattern, isRegex, caseSensitive)) {
        const newFirstLine = this.performReplacement(
          firstLine,
          matchPattern,
          replaceWith,
          replaceType,
          isRegex,
          caseSensitive,
        );

        if (newFirstLine !== firstLine) {
          const match = newFirstLine.match(
            /^(\S+)\s+(\S+)(?:\s+HTTP\/(\S+))?$/,
          );

          if (match) {
            this.logger.debug(
              `Match/Replace: "${rule.name}" applied to request first line`,
              { from: firstLine, to: newFirstLine },
            );

            return {
              ...request,
              method: match[1],
              url: match[2],
              httpVersion: match[3] || request.httpVersion,
            };
          }
        }
      }
    } else {
      const response = data as HttpResponse;
      const reason = response.statusMessage || '';
      const firstLine = `${resolveEffectiveHttpVersion({
        httpVersion: response.httpVersion,
        headers: response.rawHeaders ?? response.headers,
      })} ${response.statusCode}${reason ? ` ${reason}` : ''}`;

      if (this.testPattern(firstLine, matchPattern, isRegex, caseSensitive)) {
        const newFirstLine = this.performReplacement(
          firstLine,
          matchPattern,
          replaceWith,
          replaceType,
          isRegex,
          caseSensitive,
        );

        if (newFirstLine !== firstLine) {
          const match = newFirstLine.match(/^HTTP\/(\S+)\s+(\d+)(?:\s+(.*))?$/);

          if (match) {
            this.logger.debug(
              `Match/Replace: "${rule.name}" applied to response first line`,
              { from: firstLine, to: newFirstLine },
            );

            return {
              ...response,
              httpVersion: match[1],
              statusCode: parseInt(match[2]),
              statusMessage: match[3] || '',
            };
          }
        }
      }
    }

    return data;
  }

  private patchRawHeaderValue(
    rawHeaders: string[] | undefined,
    headerName: string,
    oldValue: string,
    newValue: string,
  ): string[] | undefined {
    if (!rawHeaders) {
      return undefined;
    }

    const patched = [...rawHeaders];

    for (let i = 0; i < patched.length - 1; i += 2) {
      if (
        patched[i].toLowerCase() === headerName.toLowerCase() &&
        patched[i + 1] === oldValue
      ) {
        patched[i + 1] = newValue;
        break;
      }
    }

    return patched;
  }

  private addRawHeader(
    rawHeaders: string[] | undefined,
    name: string,
    value: string,
  ): string[] {
    const patched = rawHeaders ? [...rawHeaders] : [];

    patched.push(name, value);

    return patched;
  }

  private removeRawHeader(
    rawHeaders: string[] | undefined,
    headerName: string,
    isRegex: boolean,
    caseSensitive: boolean,
  ): string[] | undefined {
    if (!rawHeaders) {
      return undefined;
    }

    const patched: string[] = [];

    for (let i = 0; i < rawHeaders.length - 1; i += 2) {
      if (
        !this.testPattern(rawHeaders[i], headerName, isRegex, caseSensitive)
      ) {
        patched.push(rawHeaders[i], rawHeaders[i + 1]);
      }
    }

    return patched;
  }

  private testPattern(
    value: string,
    pattern: string,
    isRegex: boolean,
    caseSensitive: boolean,
  ): boolean {
    if (!pattern) {
      return false;
    }

    try {
      if (isRegex) {
        const flags = caseSensitive ? 'g' : 'gi';
        const regex = this.getOrCompileRegex(pattern, flags);

        regex.lastIndex = 0;

        return regex.test(value);
      } else {
        const searchValue = caseSensitive ? value : value.toLowerCase();
        const searchPattern = caseSensitive ? pattern : pattern.toLowerCase();

        return searchValue.includes(searchPattern);
      }
    } catch (error) {
      this.logger.error(`Invalid pattern "${pattern}"`, {}, error as Error);

      return false;
    }
  }

  private performReplacement(
    value: string,
    pattern: string,
    replaceWith: string,
    replaceType: string,
    isRegex: boolean,
    caseSensitive: boolean,
  ): string {
    try {
      switch (replaceType) {
        case 'string': {
          if (isRegex) {
            const flags = caseSensitive ? 'g' : 'gi';
            const regex = this.getOrCompileRegex(pattern, flags);

            return value.replace(regex, replaceWith);
          } else {
            const searchValue = caseSensitive ? value : value.toLowerCase();
            const searchPattern = caseSensitive
              ? pattern
              : pattern.toLowerCase();

            if (searchValue.includes(searchPattern)) {
              const escapedPattern = pattern.replace(
                /[.*+?^${}()|[\]\\]/g,
                '\\$&',
              );
              const flags = caseSensitive ? 'g' : 'gi';
              const regex = this.getOrCompileRegex(escapedPattern, flags);

              return value.replace(regex, () => replaceWith);
            }

            return value;
          }
        }

        case 'regex': {
          const flags = caseSensitive ? 'g' : 'gi';
          const regex = this.getOrCompileRegex(pattern, flags);

          return value.replace(regex, replaceWith);
        }

        case 'function': {
          this.logger.warn(
            'Function replacement not yet implemented, using string replacement',
          );

          return replaceWith;
        }

        default:
          return value;
      }
    } catch (error) {
      this.logger.error(`Replacement error`, {}, error as Error);

      return value;
    }
  }
}
